import { useState, useEffect, useMemo } from 'react'
import { prefetchTraxxallData, buildUomRows, buildActualsMap } from '../components/TraxxallDueList'
import { buildToleranceExpiryProjections } from '../lib/utilizationProjection'
import { isOverviewItem, getInspectionLabel, getInspectionFullName } from '../lib/overviewTasks'

const HOURS_CYCLE_UOMS = new Set(['Hrs', 'Ldg', 'Enc', 'Prc', 'Ach', 'Flcy'])

// Returns { [reg]: [{ label, date, fullName }] } for overview items that are currently in
// hours/cycles tolerance, placed at the projected date when tolerance will be exhausted.
export function useToleranceExpiryDates(registrations) {
  const [allCalData, setAllCalData] = useState({})
  const regKey = [...registrations].sort().join(',')

  useEffect(() => {
    if (!registrations.length) return
    for (const reg of registrations) {
      prefetchTraxxallData(reg)
        .then(result => {
          if (!result?.items) return
          setAllCalData(prev => ({
            ...prev,
            [reg]: { items: result.items, actuals: result.actuals || [] },
          }))
        })
        .catch(() => {})
    }
  }, [regKey]) // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(() => {
    if (!Object.keys(allCalData).length) return {}
    const tolExpiryProjs = buildToleranceExpiryProjections(allCalData)
    const out = {}

    for (const [reg, d] of Object.entries(allCalData)) {
      if (!d?.items?.length) continue
      const regProjs = tolExpiryProjs[reg]
      if (!regProjs) continue

      const actualsMap = buildActualsMap(d.actuals || [])
      const entries    = []

      for (const item of d.items) {
        if (!isOverviewItem(item)) continue

        const rows = buildUomRows(item, actualsMap)
        const isInHrsTolerance = rows.some(r => !r.isApprox && r.tolRem != null && HOURS_CYCLE_UOMS.has(r.uom))
        if (!isInHrsTolerance) continue

        const label = getInspectionLabel(item)
        if (!label) continue

        const proj = regProjs.get(item)
        if (!proj?.projDate) continue

        entries.push({ label, date: proj.projDate, fullName: item['description'] || getInspectionFullName(item) || label })
      }

      if (entries.length) out[reg] = entries
    }

    return out
  }, [allCalData])
}
